import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { LazyImage } from '../hooks/useLazyLoad';
import useWishlistStore from '../store/wishlistStore';
import { toast } from 'sonner';

/**
 * Calculate stock status from product variants
 */
const getStockInfo = (product) => {
  if (!product.variants || product.variants.length === 0) {
    return { totalStock: product.in_stock ? 100 : 0, minStock: product.in_stock ? 100 : 0 };
  }
  
  const stocks = product.variants.map(v => v.stock);
  const totalStock = stocks.reduce((sum, s) => sum + s, 0);
  const minStock = Math.min(...stocks.filter(s => s > 0), Infinity);
  const hasAnyStock = totalStock > 0;
  
  return { totalStock, minStock: hasAnyStock ? minStock : 0, hasAnyStock };
};

export const ProductCard = ({ product, showStockBadge = true }) => {
  const { totalStock, minStock, hasAnyStock } = getStockInfo(product);
  const isOutOfStock = totalStock === 0;
  const isLowStock = hasAnyStock && minStock > 0 && minStock < 3;
  
  const { isInWishlist, toggleItem } = useWishlistStore();
  const inWishlist = isInWishlist(product.id);
  
  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleItem(product);
    if (added) {
      toast.success('Added to Wishlist');
    } else {
      toast.success('Removed from Wishlist');
    }
  };
  
  return (
    <div className="group relative" data-testid={`product-card-${product.id}`}>
      {/* Wishlist Button */}
      <button
        onClick={handleWishlistClick}
        className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
          inWishlist 
            ? 'bg-red-500 text-white' 
            : 'bg-white/80 backdrop-blur-sm text-[#1A1A1A] hover:bg-white hover:text-red-500'
        }`}
        data-testid={`wishlist-btn-${product.id}`}
        aria-label={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
      >
        <Heart className={`w-4 h-4 ${inWishlist ? 'fill-current' : ''}`} strokeWidth={1.5} />
      </button>

      <Link to={`/product/${product.id}`} className="block">
        {/* Image Container */}
        <div className="aspect-[3/4] overflow-hidden bg-[#F5F5F5] mb-3 relative">
          <LazyImage 
            src={product.images?.[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80'} 
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${isOutOfStock ? 'opacity-50' : ''}`}
          />
          
          {/* Discount Badge */}
          {product.discount_price && !isOutOfStock && (
            <div className="absolute top-3 left-3 bg-[#1A1A1A] text-white px-2.5 py-1 text-[10px] tracking-wider uppercase font-medium">
              {Math.round(((product.price - product.discount_price) / product.price) * 100)}% Off
            </div>
          )}
          
          {/* Stock Badges */}
          {showStockBadge && isOutOfStock && (
            <div 
              className="absolute bottom-3 left-3 bg-[#1A1A1A]/80 text-white px-2.5 py-1 text-[10px] tracking-wider uppercase font-medium"
              data-testid={`out-of-stock-badge-${product.id}`}
            >
              Sold Out
            </div>
          )}
          
          {showStockBadge && isLowStock && !isOutOfStock && (
            <div 
              className="absolute bottom-3 left-3 bg-[#C59746] text-white px-2.5 py-1 text-[10px] tracking-wider uppercase font-medium"
              data-testid={`low-stock-badge-${product.id}`}
            >
              Only {minStock} Left
            </div>
          )}
          
          {/* Out of Stock Overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-white/40 flex items-center justify-center">
              <span className="text-xs tracking-wider uppercase text-[#1A1A1A] font-medium">
                Notify Me
              </span>
            </div>
          )}
        </div>
        
        {/* Product Info */}
        <div className="space-y-1.5">
          <h3 className="text-sm text-[#1A1A1A] group-hover:text-[#C59746] transition-colors duration-300 line-clamp-2 leading-snug">
            {product.name}
          </h3>
          
          <div className="flex items-baseline gap-2">
            {product.discount_price ? (
              <>
                <span className="text-base font-medium text-[#1A1A1A]">₹{product.discount_price.toLocaleString()}</span>
                <span className="text-sm text-[#1A1A1A]/50 line-through">₹{product.price.toLocaleString()}</span>
              </>
            ) : (
              <span className="text-base font-medium text-[#1A1A1A]">₹{product.price.toLocaleString()}</span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
